import { FC, useContext, useEffect, useMemo, useRef } from "react";
import MultiselectNestedDropdown from "@app/shared/checkbox-dropdown/MultiselectNestedDropdown";
import { ModalLink } from "@app/shared/Modals";
import ManageSavedFilters from "@app/shared/manage-saved-filters/ManageSavedFilters";
import MultiselectDropdown from "@app/shared/checkbox-dropdown/MultiselectDropdown";
import { QESButton } from "@app/shared/qes-standards/Button";
import { EnterpriseComplianceFiltersContext } from "@app/overviews/enterprise-compliance-dashboard/contexts/EcdFiltersContext";
import EnterpriseComplianceFiltersOverview from "@app/overviews/enterprise-compliance-dashboard/EcdFiltersOverview";

interface EcdFiltersProps {
  isDesktopSized: boolean;
}

const EnterpriseComplianceDashboardFilters: FC<EcdFiltersProps> = (props: EcdFiltersProps) => {
  const filtersContext = useContext(EnterpriseComplianceFiltersContext);
  const loadSavedFiltersRef = useRef<HTMLSelectElement>(null);

  // this is set up to show the name of the loaded filter set as the dropdown
  // value right up until the filter selections are changed
  // (it will also reset if changes are made to the filter sets in storage for other reasons)
  useEffect(() => {
    if (loadSavedFiltersRef.current && !filtersContext.hasLoadedFiltersFromStorage) {
      loadSavedFiltersRef.current.value = "";
    }
  }, [
    filtersContext.filterTags,
    filtersContext.filterLineOfBusiness,
    filtersContext.filterServiceArea,
    filtersContext.filterSpecialty,
    filtersContext.filterCountyStatus,
    filtersContext.filterProjects
  ]);

  const applyButton = (
    <QESButton
      qesType="secondary"
      qesTestId="enterprise-compliance-dashboard__apply-filters"
      onClick={() => filtersContext.applyFilters()}
      className="col-6 col-xl-auto"
    >
      Apply Filters
    </QESButton>
  );

  const resetButton = (
    <ModalLink
      classNames="qes-button qes-button-secondary danger col-6 col-xl-auto"
      content={{
        body: "This will reset all filters to their defaults and apply them.",
        cancelButtonText: "Cancel",
        confirmButtonAction: filtersContext.resetFilters,
        confirmButtonText: "Reset",
        confirmButtonType: "primary-danger",
        title: "Reset Filters",
      }}
      testId="enterprise-compliance-dashboard__reset-filters"
      hideBodyScroll={true}
    >
      Reset Filters
    </ModalLink>
  );

  const getSelectionText = (currentSelections: unknown[], totalOptions: unknown[]) => {
    const current = currentSelections.length;
    const total = totalOptions.length;
    if (total === 0 && !filtersContext.apiReadyFilters) {
      return "Loading";
    }
    return `${current.toLocaleString()} of ${total.toLocaleString()} item(s) selected`;
  }

  const onLoadSavedFilters = (e: string) => {
    if (!e) {
      return;
    }
    const loadedFilters = filtersContext.loadCustomFilterSet(e);
    filtersContext.applyCustomFilterSet(loadedFilters);
  }

  const tagsSelectionText = useMemo(() => {
    return getSelectionText(filtersContext.filterTags, filtersContext.tagsOptions.flatMap((tagGroup) => tagGroup.children));
  }, [filtersContext.filterTags, filtersContext.tagsOptions]);

  const lobSelectionText = useMemo(() => {
    return getSelectionText(filtersContext.filterLineOfBusiness, filtersContext.LOBOptions);
  }, [filtersContext.filterLineOfBusiness, filtersContext.LOBOptions]);

  const serviceAreaSelectionText = useMemo(() => {
    return getSelectionText(filtersContext.filterServiceArea, filtersContext.serviceAreaOptions.flatMap((sa) => sa.children));
  }, [filtersContext.filterServiceArea, filtersContext.serviceAreaOptions]);

  const specialtySelectionText = useMemo(() => {
    return getSelectionText(filtersContext.filterSpecialty, filtersContext.specialtyOptions);
  }, [filtersContext.filterSpecialty, filtersContext.specialtyOptions]);

  const countyStatusSelectionText = useMemo(() => {
    return getSelectionText(filtersContext.filterCountyStatus, filtersContext.countyStatusOptions);
  }, [filtersContext.filterCountyStatus, filtersContext.countyStatusOptions]);

  const projectsSelectionText = useMemo(() => {
    return getSelectionText(filtersContext.filterProjects, filtersContext.projectsOptions);
  }, [filtersContext.filterProjects, filtersContext.projectsOptions]);

  const onSubmit = async (filtersToDelete: Set<string>, newFilterNames: Map<string, string>) => {
    // first make any deletions
    filtersContext.deleteCustomFilterSets([...filtersToDelete]);

    // then make name changes
    [...newFilterNames].forEach(([oldName, newName]) => {
      // first make sure deleted filters aren't in the name changes map too
      if (filtersToDelete.has(oldName) || newName === oldName || newName === "" || newName === "default") {
        return;
      }
      // then make a new filter set and delete the old one
      const savedFilters = filtersContext.loadCustomFilterSet(oldName);
      filtersContext.saveCustomFilterSet(newName, savedFilters);
      filtersContext.deleteCustomFilterSets([oldName]);
    });
  }

  return (
    <div className="ecd-filters">
      <ManageSavedFilters loadSavedFiltersList={async () => filtersContext.getFilterSetNames()}
                          onFilterChange={onLoadSavedFilters} onSubmit={onSubmit}
                          filterValue={filtersContext.loadedFilterSet}/>
      <div className="mb-3 row justify-content-start">
        <MultiselectNestedDropdown
          containerClassName="col-12 col-xl"
          customSelectInputText={tagsSelectionText}
          disableParentSearch={true}
          hideParentCheckboxes={true}
          items={filtersContext.tagsOptions}
          label="Tags"
          onSelectionChanged={(items) => filtersContext.setFilterTags(items)}
          selectedItems={filtersContext.filterTags}
          testId="enterprise-compliance-dashboard__tags"
          showTooltipOnOverflow={true}
        />

        <MultiselectDropdown
          containerClassName="col-12 col-xl"
          customSelectInputText={lobSelectionText}
          customWidth={true}
          items={filtersContext.LOBOptions}
          label="Line of Business"
          onSelectionChanged={(selectedItems) => filtersContext.setFilterLineOfBusiness(selectedItems)}
          selectedItems={filtersContext.filterLineOfBusiness}
          testId="enterprise-compliance-filters__line-of-business"
          showTooltipOnOverflow={true}
        />

        <MultiselectNestedDropdown
          containerClassName="col-12 col-xl"
          customSelectInputText={serviceAreaSelectionText}
          disableParentSearch={true}
          items={filtersContext.serviceAreaOptions}
          label="Service Area"
          onSelectionChanged={(items) => filtersContext.setFilterServiceArea(items)}
          selectedItems={filtersContext.filterServiceArea}
          testId="enterprise-compliance-dashboard__service-area"
        />

        <MultiselectDropdown
          containerClassName="col-12 col-xl"
          customWidth={true}
          customSelectInputText={specialtySelectionText}
          items={filtersContext.specialtyOptions}
          label="Specialty"
          onSelectionChanged={(selectedItems) => filtersContext.setFilterSpecialty(selectedItems)}
          selectedItems={filtersContext.filterSpecialty}
          testId="enterprise-compliance-filters__specialty"
          showTooltipOnOverflow={true}
        />

        <MultiselectDropdown
          containerClassName="col-12 col-xl"
          customWidth={true}
          customSelectInputText={countyStatusSelectionText}
          items={filtersContext.countyStatusOptions}
          label="County Status"
          onSelectionChanged={(selectedItems) => filtersContext.setFilterCountyStatus(selectedItems)}
          selectedItems={filtersContext.filterCountyStatus}
          testId="enterprise-compliance-filters__county-status"
        />

        <MultiselectDropdown
          containerClassName="col-12 col-xl"
          customWidth={true}
          customSelectInputText={projectsSelectionText}
          items={filtersContext.projectsOptions}
          label="Projects"
          onSelectionChanged={(selectedItems) => filtersContext.setFilterProjects(selectedItems)}
          selectedItems={filtersContext.filterProjects}
          testId="enterprise-compliance-filters__projects"
          showTooltipOnOverflow={true}
        />

        <span className="enterprise-compliance-dashboard__filter-buttons col-12 col-xl-auto">
                    {props.isDesktopSized ? (
                      <>
                        {applyButton}
                        {resetButton}
                      </>
                    ) : (
                      <>
                        {resetButton}
                        {applyButton}
                      </>
                    )}
                </span>
      </div>
      <EnterpriseComplianceFiltersOverview/>
    </div>
  );
}

export default EnterpriseComplianceDashboardFilters;
